import { format } from 'date-fns';
import { Frown } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  Card,
  CardHeader,
  CardDescription,
  CardTitle,
  CardContent,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';
import { RESOLUTION_STATUS_COLORS } from '@/Domain/hooks/Analytics/resolutionStatusColors';
import { toKey } from '@/Domain/hooks/Analytics/useConversationAnalytics.hook';

// Only the outermost exposed edges of the column get rounded corners
function getRadius(index: number, total: number): [number, number, number, number] {
  if (total === 1) return [5, 5, 5, 5];
  if (index === 0) return [0, 0, 5, 5];
  if (index === total - 1) return [5, 5, 0, 0];
  return [0, 0, 0, 0];
}

type Props = {
  data: any[];
  labels: readonly string[];
  isLoading: boolean;
};

export const OutcomeResultsChart = ({ data, labels, isLoading }: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.conversation_outcome' });

  const hasData = data && data.length > 0;

  const chartConfig = useMemo(() => {
    const config: Record<string, { label: string; color: string }> = {};
    for (const label of labels) {
      const key = toKey(label);
      config[key] = {
        label: t(key, label),
        color: RESOLUTION_STATUS_COLORS[key] || 'var(--chart-negative)',
      };
    }
    return config;
  }, [labels, t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <ChartPlaceholder
            icon={Frown}
            titleTranslationKey="no_data_in_range.title"
            descriptionTranslationKey="no_data_in_range.description"
            spacing="small"
          />
        ) : (
          <ChartContainer
            config={chartConfig}
            className={`h-60 w-full ${isLoading ? 'animate-pulse' : ''}`}
          >
            <BarChart accessibilityLayer data={data} margin={{ left: 0, right: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => format(new Date(value), 'dd MMM')}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
                width={36}
              />
              <ChartTooltip
                cursor={false}
                wrapperStyle={{ whiteSpace: 'normal' }}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    className="w-auto"
                    labelFormatter={(value) => format(new Date(value), 'dd MMM yyyy')}
                  />
                }
              />
              <ChartLegend
                content={({ payload }) => (
                  <ChartLegendContent payload={[...(payload ?? [])].reverse()} />
                )}
              />
              {labels.map((label, idx) => {
                const key = toKey(label);
                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="a"
                    fill={`var(--color-${key})`}
                    radius={getRadius(idx, labels.length)}
                  />
                );
              })}
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
